import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePortal, API_BASE } from './HousePortalLayout';

// ═══════════════════════════════════════════════════════════
// i18n
// ═══════════════════════════════════════════════════════════
const T = {
  en: {
    title: 'Find Your Perfect Property',
    officeTitle: 'Bangkok Office Rental Search',
    homeTitle: 'Bangkok Home Rental Search',
    allLabel: 'All Properties',
    officeLabel: 'Office & Coworking',
    homeLabel: 'Homes & Condos',
    results: 'properties found',
    noResults: 'No properties match your criteria. Try adjusting your filters.',
    loading: 'Searching properties...',
    filters: 'Filters',
    type: 'Property Type',
    condo: 'Condo', house: 'House', villa: 'Villa', townhouse: 'Townhouse',
    apartment: 'Apartment', office: 'Office', coworking: 'Coworking',
    priceRange: 'Price Range',
    priceMax: 'Max Price (THB/month)',
    areaMin: 'Min Area (sqm)',
    bedrooms: 'Bedrooms',
    district: 'District',
    furnishing: 'Furnishing',
    furnished: 'Furnished', unfurnished: 'Unfurnished', partly: 'Partly Furnished',
    source: 'Data Source',
    allSources: 'All Sources',
    poi: 'Nearby Facilities',
    rating: 'Min Rating',
    sortBy: 'Sort By',
    newest: 'Newest', priceLow: 'Price: Low to High', priceHigh: 'Price: High to Low',
    areaHigh: 'Area: High to Low', perPing: 'Price per Ping', ratingHigh: 'Rating',
    clearAll: 'Clear All',
    loadMore: 'Load More',
    beds: 'Beds', baths: 'Baths', sqm: 'sqm',
    hot: 'HOT',
    viewDetails: 'View Details',
    hideFilters: 'Hide Filters',
    showFilters: 'Show Filters',
    any: 'Any',
    min: 'Min', max: 'Max',
    perMonth: '/month',
    perSqmMonth: '/sqm·month',
    ping: 'ping',
    perPingUnit: 'per ping',
    lineAsk: 'Ask on LINE',
    nearbyFacilities: 'Nearby facilities',
    nearbyStay: 'Nearby stays',
    noRating: 'No rating',
    apply: 'Apply',
    popularAreas: 'Popular areas',
  },
  th: {
    title: '\u0e04\u0e49\u0e19\u0e2b\u0e32\u0e2d\u0e2a\u0e31\u0e07\u0e2b\u0e32\u0e23\u0e34\u0e21\u0e17\u0e23\u0e31\u0e1e\u0e22\u0e4c\u0e17\u0e35\u0e48\u0e2a\u0e21\u0e1a\u0e39\u0e23\u0e13\u0e4c\u0e41\u0e1a\u0e1a',
    officeTitle: '\u0e04\u0e49\u0e19\u0e2b\u0e32\u0e2a\u0e33\u0e19\u0e31\u0e01\u0e07\u0e32\u0e19\u0e40\u0e0a\u0e48\u0e32\u0e01\u0e23\u0e38\u0e07\u0e40\u0e17\u0e1e',
    homeTitle: '\u0e04\u0e49\u0e19\u0e2b\u0e32\u0e1a\u0e49\u0e32\u0e19\u0e40\u0e0a\u0e48\u0e32\u0e01\u0e23\u0e38\u0e07\u0e40\u0e17\u0e1e',
    allLabel: '\u0e2d\u0e2a\u0e31\u0e07\u0e2b\u0e32\u0e23\u0e34\u0e21\u0e17\u0e23\u0e31\u0e1e\u0e22\u0e4c\u0e17\u0e31\u0e49\u0e07\u0e2b\u0e21\u0e14',
    officeLabel: '\u0e2a\u0e33\u0e19\u0e31\u0e01\u0e07\u0e32\u0e19 & \u0e2a\u0e31\u0e07\u0e04\u0e21',
    homeLabel: '\u0e1a\u0e49\u0e32\u0e19 & \u0e04\u0e2d\u0e19\u0e42\u0e14',
    results: '\u0e23\u0e32\u0e22\u0e01\u0e32\u0e23\u0e17\u0e35\u0e48\u0e1e\u0e1a',
    noResults: '\u0e44\u0e21\u0e48\u0e1e\u0e1a\u0e2d\u0e2a\u0e31\u0e07\u0e2b\u0e32\u0e23\u0e34\u0e21\u0e17\u0e23\u0e31\u0e1e\u0e22\u0e4c\u0e15\u0e32\u0e21\u0e40\u0e01\u0e13\u0e11\u0e4c',
    loading: '\u0e01\u0e33\u0e25\u0e31\u0e07\u0e04\u0e49\u0e19\u0e2b\u0e32...',
    filters: '\u0e15\u0e31\u0e27\u0e01\u0e23\u0e2d\u0e07',
    type: '\u0e1b\u0e23\u0e30\u0e40\u0e20\u0e17\u0e2d\u0e2a\u0e31\u0e07\u0e2b\u0e32\u0e23\u0e34\u0e21\u0e17\u0e23\u0e31\u0e1e\u0e22\u0e4c',
    condo: '\u0e04\u0e2d\u0e19\u0e42\u0e14', house: '\u0e1a\u0e49\u0e32\u0e19', villa: '\u0e27\u0e34\u0e25\u0e25\u0e48\u0e32', townhouse: '\u0e17\u0e32\u0e27\u0e19\u0e4c\u0e40\u0e2e\u0e49\u0e32\u0e2a\u0e4c',
    apartment: '\u0e2d\u0e32\u0e1e\u0e32\u0e23\u0e4c\u0e17\u0e40\u0e21\u0e19\u0e17\u0e4c', office: '\u0e2a\u0e33\u0e19\u0e31\u0e01\u0e07\u0e32\u0e19', coworking: '\u0e2a\u0e31\u0e07\u0e04\u0e21',
    priceRange: '\u0e0a\u0e48\u0e27\u0e07\u0e23\u0e32\u0e04\u0e32',
    priceMax: '\u0e23\u0e32\u0e04\u0e32\u0e2a\u0e39\u0e07\u0e2a\u0e38\u0e14 (\u0e1a\u0e32\u0e17/\u0e40\u0e14\u0e37\u0e2d\u0e19)',
    areaMin: '\u0e1e\u0e37\u0e49\u0e19\u0e17\u0e35\u0e48\u0e02\u0e31\u0e49\u0e19\u0e15\u0e48\u0e33 (\u0e15\u0e23.\u0e21.)',
    bedrooms: '\u0e2b\u0e49\u0e2d\u0e07\u0e19\u0e2d\u0e19',
    district: '\u0e40\u0e02\u0e15',
    furnishing: '\u0e40\u0e1f\u0e2d\u0e23\u0e4c\u0e19\u0e34\u0e40\u0e08\u0e2d\u0e23\u0e4c',
    furnished: '\u0e15\u0e01\u0e41\u0e15\u0e48\u0e07\u0e04\u0e23\u0e2d\u0e1a', unfurnished: '\u0e44\u0e21\u0e48\u0e15\u0e01\u0e41\u0e15\u0e48\u0e07', partly: '\u0e15\u0e01\u0e41\u0e15\u0e48\u0e07\u0e1a\u0e32\u0e07\u0e2a\u0e48\u0e27\u0e19',
    source: '\u0e41\u0e2b\u0e25\u0e48\u0e07\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25',
    allSources: '\u0e17\u0e31\u0e49\u0e07\u0e2b\u0e21\u0e14',
    poi: '\u0e2a\u0e34\u0e48\u0e07\u0e2d\u0e33\u0e19\u0e27\u0e22\u0e43\u0e01\u0e25\u0e49\u0e40\u0e04\u0e35\u0e22\u0e07',
    rating: '\u0e04\u0e30\u0e41\u0e19\u0e19\u0e02\u0e31\u0e49\u0e19\u0e15\u0e48\u0e33',
    sortBy: '\u0e40\u0e23\u0e35\u0e22\u0e07\u0e15\u0e32\u0e21',
    newest: '\u0e43\u0e2b\u0e21\u0e48\u0e2a\u0e38\u0e14', priceLow: '\u0e23\u0e32\u0e04\u0e32: \u0e15\u0e48\u0e33\u0e44\u0e1b\u0e2a\u0e39\u0e07', priceHigh: '\u0e23\u0e32\u0e04\u0e32: \u0e2a\u0e39\u0e07\u0e44\u0e1b\u0e15\u0e48\u0e33',
    areaHigh: '\u0e1e\u0e37\u0e49\u0e19\u0e17\u0e35\u0e48: \u0e21\u0e32\u0e01\u0e44\u0e1b\u0e19\u0e49\u0e2d\u0e22', perPing: '\u0e23\u0e32\u0e04\u0e32\u0e15\u0e48\u0e2d\u0e1e\u0e37\u0e49\u0e19\u0e17\u0e35\u0e48', ratingHigh: '\u0e04\u0e30\u0e41\u0e19\u0e19',
    clearAll: '\u0e25\u0e49\u0e32\u0e07\u0e17\u0e31\u0e49\u0e07\u0e2b\u0e21\u0e14',
    loadMore: '\u0e42\u0e2b\u0e25\u0e14\u0e40\u0e1e\u0e34\u0e48\u0e21',
    beds: '\u0e2b\u0e49\u0e2d\u0e07\u0e19\u0e2d\u0e19', baths: '\u0e2b\u0e49\u0e2d\u0e07\u0e19\u0e49\u0e33', sqm: '\u0e15\u0e23.\u0e21.',
    hot: 'HOT',
    viewDetails: '\u0e14\u0e39\u0e23\u0e32\u0e22\u0e25\u0e30\u0e40\u0e2d\u0e35\u0e22\u0e14',
    hideFilters: '\u0e0b\u0e48\u0e2d\u0e19\u0e15\u0e31\u0e27\u0e01\u0e23\u0e2d\u0e07',
    showFilters: '\u0e41\u0e2a\u0e14\u0e07\u0e15\u0e31\u0e27\u0e01\u0e23\u0e2d\u0e07',
    any: '\u0e17\u0e31\u0e49\u0e07\u0e2b\u0e21\u0e14',
    min: '\u0e15\u0e48\u0e33\u0e2a\u0e38\u0e14', max: '\u0e2a\u0e39\u0e07\u0e2a\u0e38\u0e14',
    perMonth: '/\u0e40\u0e14\u0e37\u0e2d\u0e19',
    perSqmMonth: '/\u0e15\u0e23.\u0e21.\u0e40\u0e14\u0e37\u0e2d\u0e19',
    ping: '\u0e1e\u0e37\u0e49\u0e19\u0e17\u0e35\u0e48',
    perPingUnit: '/\u0e1e\u0e37\u0e49\u0e19\u0e17\u0e35\u0e48',
    lineAsk: '\u0e2a\u0e2d\u0e1a\u0e16\u0e32\u0e21\u0e17\u0e32\u0e07 LINE',
    nearbyFacilities: '\u0e2a\u0e34\u0e48\u0e07\u0e2d\u0e33\u0e19\u0e27\u0e22\u0e43\u0e01\u0e25\u0e49\u0e40\u0e04\u0e35\u0e22\u0e07',
    nearbyStay: '\u0e17\u0e35\u0e48\u0e1e\u0e31\u0e01\u0e43\u0e01\u0e25\u0e49\u0e40\u0e04\u0e35\u0e22\u0e07',
    noRating: '\u0e44\u0e21\u0e48\u0e21\u0e35\u0e04\u0e30\u0e41\u0e19\u0e19',
    apply: '\u0e19\u0e33\u0e44\u0e1b\u0e43\u0e0a\u0e49',
    popularAreas: '\u0e40\u0e02\u0e15\u0e22\u0e2d\u0e14\u0e19\u0e34\u0e22\u0e21',
  },
  zh: {
    title: '\u627e\u5230\u60a8\u7684\u5b8c\u7f8e\u623f\u7522',
    officeTitle: '\u66fc\u8c37\u8fa6\u516c\u5ba4\u79df\u8cc1\u641c\u5c0b',
    homeTitle: '\u66fc\u8c37\u4f4f\u5bb6\u79df\u5c4b\u641c\u5c0b',
    allLabel: '\u5168\u90e8\u623f\u7522',
    officeLabel: '\u8fa6\u516c\u5ba4 & \u5171\u4eab\u8fa6\u516c',
    homeLabel: '\u4f4f\u5bb6 & \u516c\u5bd3',
    results: '\u500b\u623f\u7522\u7d50\u679c',
    noResults: '\u6c92\u6709\u7b26\u5408\u689d\u4ef6\u7684\u623f\u7522\u3002',
    loading: '\u6b63\u5728\u641c\u5c0b...',
    filters: '\u7be9\u9078',
    type: '\u623f\u7522\u985e\u578b',
    condo: '\u516c\u5bd3', house: '\u5225\u5885', villa: '\u5225\u5885', townhouse: '\u806f\u6392\u5225\u5885',
    apartment: '\u516c\u5bd3', office: '\u8fa6\u516c\u5ba4', coworking: '\u5171\u4eab\u8fa6\u516c',
    priceRange: '\u50f9\u683c\u7bc4\u570d',
    priceMax: '\u6700\u9ad8\u50f9\u683c (\u6cf0\u9291/\u6708)',
    areaMin: '\u6700\u5c0f\u9762\u7a4d (\u5e73\u65b9\u7c73)',
    bedrooms: '\u81e5\u5ba4',
    district: '\u5340\u57df',
    furnishing: '\u88dd\u4fee',
    furnished: '\u5e36\u50a2\u4ff1', unfurnished: '\u4e0d\u5e36\u50a2\u4ff1', partly: '\u90e8\u5206\u50a2\u4ff1',
    source: '\u8cc7\u6599\u4f86\u6e90',
    allSources: '\u5168\u90e8',
    poi: '\u9644\u8fd1\u8a2d\u65bd',
    rating: '\u8a55\u5206\u7be9\u9078',
    sortBy: '\u6392\u5e8f',
    newest: '\u6700\u65b0', priceLow: '\u50f9\u683c\u4f4e\u5230\u9ad8', priceHigh: '\u50f9\u683c\u9ad8\u5230\u4f4e',
    areaHigh: '\u9762\u7a4d\u5927\u5230\u5c0f', perPing: '\u6bcf\u576a\u55ae\u50f9', ratingHigh: '\u8a55\u5206',
    clearAll: '\u6e05\u9664\u5168\u90e8',
    loadMore: '\u8f09\u5165\u66f4\u591a',
    beds: '\u81e5', baths: '\u6d74', sqm: '\u5e73\u65b9\u7c73',
    hot: 'HOT',
    viewDetails: '\u67e5\u770b\u8a73\u60c5',
    hideFilters: '\u96b1\u85cf\u7be9\u9078',
    showFilters: '\u986f\u793a\u7be9\u9078',
    any: '\u4efb\u610f',
    min: '\u6700\u4f4e', max: '\u6700\u9ad8',
    perMonth: '/\u6708',
    perSqmMonth: '/\u5e73\u65b9\u7c73\u00b7\u6708',
    ping: '\u576a',
    perPingUnit: '/\u576a',
    lineAsk: 'LINE \u8a62\u554f',
    nearbyFacilities: '\u9644\u8fd1\u8a2d\u65bd',
    nearbyStay: '\u9644\u8fd1\u4f4f\u5bbf',
    noRating: '\u66ab\u7121\u8a55\u5206',
    apply: '\u78ba\u5b9a',
    popularAreas: '\u71b1\u9580\u5340\u57df',
  },
};

// 1 ping = 3.305785 sqm
const SQM_PER_PING = 3.305785;
const toPing = (sqm) => (sqm ? sqm / SQM_PER_PING : null);
const fmtPing = (sqm) => {
  const p = toPing(sqm);
  return p ? `${p.toFixed(1)}` : null;
};
const fmtPerPing = (o) => {
  if (!o.price || !o.area_sqm) return null;
  return Math.round(o.price / (o.area_sqm / SQM_PER_PING));
};

const POI_ICONS = {
  tennis: '\ud83c\udfbe', pickleball: '\ud83c\udfd3', mall: '\ud83c\udfed', shopping_street: '\ud83d\udecd\ufe0f',
  park: '\ud83c\udf33', gym: '\ud83d\udcaa', golf: '\u26f3', fine_dining: '\ud83c\udf7d\ufe0f', convention: '\ud83c\udfdb\ufe0f',
};

const GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
];

const OFFICE_TYPES = ['office', 'coworking'];
const HOME_TYPES = ['condo', 'house', 'townhouse', 'apartment', 'villa'];
const ALL_TYPES = ['office', 'coworking', 'condo', 'house', 'townhouse', 'apartment', 'villa'];

const fmtPrice = (price, currency = 'THB') => {
  if (!price) return '-';
  if (price >= 1000000) return `${currency} ${(price / 1000000).toFixed(1)}M`;
  if (price >= 1000) return `${currency} ${(price / 1000).toFixed(0)}K`;
  return `${currency} ${price.toLocaleString()}`;
};

const buildGMap = (p) => {
  const parts = [p.address, p.district, p.city, p.country].filter(Boolean);
  if (!parts.length) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
};

export default function HousePortalSearch() {
  const { slug, lang, isMobile } = usePortal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = T[lang] || T.en;

  const initialMode = searchParams.get('mode') === 'office' ? 'office' : searchParams.get('mode') === 'home' ? 'home' : 'all';
  const [mode, setMode] = useState(initialMode);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(!isMobile);

  const [meta, setMeta] = useState(null);
  const [lineOaId, setLineOaId] = useState('');

  // Filters
  const [types, setTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [priceMax, setPriceMax] = useState('');
  const [areaMin, setAreaMin] = useState('');
  const [beds, setBeds] = useState('');
  const [districts, setDistricts] = useState([]);
  const [furnishing, setFurnishing] = useState('');
  const [poiKeys, setPoiKeys] = useState([]);
  const [minRating, setMinRating] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Init filters from URL params
  useEffect(() => {
    const district = searchParams.get('district');
    const budget = searchParams.get('budget');
    const bedsParam = searchParams.get('bedrooms');
    if (district) setDistricts([district]);
    if (bedsParam) setBeds(bedsParam);
    if (budget) {
      const parts = budget.split('-');
      if (parts[0]) setPriceMax(String(parseInt(parts[0]) * 1000000));
      if (parts[1]) setPriceMax(String(parseInt(parts[1]) * 1000000));
      if (budget.includes('+')) setPriceMax(String(parseInt(budget) * 1000000));
    }
  }, [searchParams]);

  // Load search metadata (sources / areas / POI categories) + LINE info
  useEffect(() => {
    const loadMeta = async () => {
      try {
        const res = await fetch(`${API_BASE}/h/${slug}/search-meta`);
        if (res.ok) {
          const data = await res.json();
          setMeta(data);
        }
      } catch { /* ok */ }
      try {
        const res = await fetch(`${API_BASE}/h/${slug}/line-info`);
        if (res.ok) {
          const data = await res.json();
          if (data.line_oa_id) setLineOaId(data.line_oa_id);
        }
      } catch { /* ok */ }
    };
    loadMeta();
  }, [slug]);

  // Reset filters when switching mode
  useEffect(() => {
    setTypes([]);
    setSources([]);
    setPriceMax('');
    setAreaMin('');
    setBeds('');
    setDistricts([]);
    setFurnishing('');
    setPoiKeys([]);
    setMinRating('');
    setSortBy('newest');
    setPage(1);
  }, [mode]);

  // Fetch properties
  const fetchProperties = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      let typeList = types;
      if (!typeList.length) {
        typeList = mode === 'office' ? OFFICE_TYPES : mode === 'home' ? HOME_TYPES : ALL_TYPES;
      }
      params.set('types', typeList.join(','));
      if (priceMax) params.set('price_max', priceMax);
      if (areaMin) params.set('area_min', areaMin);
      if (beds) params.set('bedrooms', beds);
      if (districts.length) params.set('districts', districts.join(','));
      if (furnishing) params.set('furnishing', furnishing);
      if (sources.length) params.set('sources', sources.join(','));
      if (poiKeys.length) params.set('poi', poiKeys.join(','));
      if (minRating) params.set('min_rating', minRating);
      const sortMap = {
        newest: ['created_at', 'desc'],
        priceLow: ['price', 'asc'],
        priceHigh: ['price', 'desc'],
        areaHigh: ['area_sqm', 'desc'],
        perPing: ['price_per_ping', 'asc'],
        ratingHigh: ['rating', 'desc'],
      };
      const [sb, so] = sortMap[sortBy] || ['created_at', 'desc'];
      params.set('sort_by', sb);
      params.set('sort_order', so);
      params.set('skip', String((pageNum - 1) * 12));
      params.set('limit', '12');

      const res = await fetch(`${API_BASE}/h/${slug}/properties?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        const items = data.items || data.properties || data || [];
        if (pageNum === 1) {
          setProperties(items);
        } else {
          setProperties(prev => [...prev, ...items]);
        }
        setTotal(data.total || items.length);
      } else {
        setProperties([]);
        setTotal(0);
      }
    } catch {
      setProperties([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [slug, mode, types, sources, priceMax, areaMin, beds, districts, furnishing, poiKeys, minRating, sortBy]);

  useEffect(() => { fetchProperties(1); }, [fetchProperties]);

  const toggleType = (type) => {
    setTypes(prev => prev.includes(type) ? prev.filter(x => x !== type) : [...prev, type]);
  };

  const toggleSource = (key) => {
    setSources(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
  };

  const toggleDistrict = (d) => {
    setDistricts(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const togglePoi = (k) => {
    setPoiKeys(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  };

  const clearFilters = () => {
    setTypes([]); setSources([]); setPriceMax(''); setAreaMin(''); setBeds('');
    setDistricts([]); setFurnishing(''); setPoiKeys([]); setMinRating(''); setSortBy('newest');
  };

  const activeFilterCount = types.length + sources.length + (priceMax ? 1 : 0) + (areaMin ? 1 : 0) + (beds ? 1 : 0) + districts.length + (furnishing ? 1 : 0) + poiKeys.length + (minRating ? 1 : 0);

  const filterSectionStyle = { marginBottom: 24 };
  const filterLabelStyle = {
    fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 10,
    textTransform: 'uppercase', letterSpacing: 0.8, display: 'block',
  };

  const isOfficeMode = mode === 'office';
  const sourceList = isOfficeMode
    ? (meta?.office_sources || [])
    : mode === 'home'
      ? (meta?.home_sources || [])
      : [...(meta?.office_sources || []), ...(meta?.home_sources || [])];
  const areaList = isOfficeMode
    ? (meta?.office_areas || [])
    : mode === 'home'
      ? (meta?.home_areas || [])
      : [...(meta?.office_areas || []), ...(meta?.home_areas || [])];
  const poiCategories = meta?.poi_categories || [];

  const buildLineChatUrl = (p) => {
    if (!lineOaId) return null;
    const oa = (lineOaId.startsWith('@') || lineOaId.startsWith('U')) ? lineOaId : `@${lineOaId}`;
    const area = p.district || '';
    const price = p.price ? `${fmtPrice(p.price)}/月` : '';
    const label = p.property_type === 'coworking' ? '共享辦公室' : p.property_type === 'office' ? '辦公室' : '住家物件';
    const text = `您好！我想詢問這個物件：\n📍 ${p.title}\n🏢 ${area}\n💰 ${price}\n\n（物件編號 Ref#${p.id}）\n方便請協助安排參觀，謝謝！`;
    return `https://line.me/R/oaMessage/${oa}/?text=${encodeURIComponent(text)}`;
  };

  const typeLabel = (p) => {
    const key = p.property_type;
    if (key === 'coworking') return t.coworking;
    if (key === 'office') return t.office;
    return t[key] || key;
  };

  const modeTitle = mode === 'office' ? t.officeTitle : mode === 'home' ? t.homeTitle : t.title;

  return (
    <div style={{ background: '#f5f5fa', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
        padding: isMobile ? '32px 24px 24px' : '48px 48px 36px',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h1 style={{
            fontSize: isMobile ? 28 : 40, fontWeight: 800, color: '#fff',
            letterSpacing: -1, marginBottom: 8,
          }}>
            {modeTitle}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>
              {total} {t.results}
            </span>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} style={{
                background: 'rgba(232,54,58,0.15)', border: '1px solid rgba(232,54,58,0.3)',
                color: '#e8363a', padding: '4px 14px', borderRadius: 20,
                fontSize: 13, cursor: 'pointer', fontWeight: 500,
              }}>
                {t.clearAll} ({activeFilterCount})
              </button>
            )}
          </div>

          {/* Mode Tabs */}
          <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { key: 'all', label: t.allLabel },
              { key: 'office', label: t.officeLabel },
              { key: 'home', label: t.homeLabel },
            ].map(m => (
              <button
                key={m.key}
                onClick={() => {
                  setMode(m.key);
                  navigate(`/h/${slug}/properties${m.key === 'all' ? '' : `?mode=${m.key}`}`, { replace: true });
                }}
                style={{
                  padding: '10px 22px', borderRadius: 24,
                  border: mode === m.key ? '2px solid #d4a537' : '2px solid rgba(255,255,255,0.2)',
                  background: mode === m.key ? 'rgba(212,165,55,0.15)' : 'rgba(255,255,255,0.05)',
                  color: mode === m.key ? '#f0c75e' : 'rgba(255,255,255,0.7)',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  transition: 'all 0.25s',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{
        maxWidth: 1200, margin: '0 auto',
        padding: isMobile ? '24px 16px' : '32px 48px',
        display: 'flex', gap: 32,
        flexDirection: isMobile ? 'column' : 'row',
      }}>

        {/* Filters Toggle (Mobile) */}
        {isMobile && (
          <button onClick={() => setShowFilters(!showFilters)} style={{
            background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
            padding: '12px 20px', borderRadius: 12, fontSize: 14, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, color: '#1a1a2e',
          }}>
            &#9776; {showFilters ? t.hideFilters : t.showFilters}
            {activeFilterCount > 0 && (
              <span style={{
                background: '#e8363a', color: '#fff', width: 20, height: 20,
                borderRadius: '50%', display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 11, fontWeight: 700,
              }}>{activeFilterCount}</span>
            )}
          </button>
        )}

        {/* ═══ SIDEBAR FILTERS ═══ */}
        {showFilters && (
          <div style={{
            width: isMobile ? '100%' : 280, flexShrink: 0,
            background: '#fff', borderRadius: 20, padding: 24,
            boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
            alignSelf: 'flex-start',
            position: isMobile ? 'relative' : 'sticky',
            top: isMobile ? 'auto' : 104,
            maxHeight: isMobile ? 'none' : 'calc(100vh - 120px)',
            overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>{t.filters}</h3>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} style={{
                  background: 'none', border: 'none', color: '#e8363a',
                  fontSize: 13, cursor: 'pointer', fontWeight: 500,
                }}>{t.clearAll}</button>
              )}
            </div>

            {/* Data Source */}
            {sourceList.length > 0 && (
              <div style={filterSectionStyle}>
                <span style={filterLabelStyle}>{t.source}</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {sourceList.map(s => (
                    <button key={s.key} onClick={() => toggleSource(s.key)} style={{
                      padding: '6px 12px', borderRadius: 8,
                      border: sources.includes(s.key) ? '1px solid #e8363a' : '1px solid rgba(0,0,0,0.06)',
                      background: sources.includes(s.key) ? 'rgba(232,54,58,0.06)' : '#f9f9fb',
                      color: sources.includes(s.key) ? '#e8363a' : '#888',
                      fontSize: 12, cursor: 'pointer', fontWeight: 500,
                      transition: 'all 0.2s',
                    }}>
                      {s.name_zh || s.name} ({s.count})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Property Type */}
            <div style={filterSectionStyle}>
              <span style={filterLabelStyle}>{t.type}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(mode === 'all' ? ALL_TYPES : isOfficeMode ? OFFICE_TYPES : HOME_TYPES).map(type => (
                  <button key={type} onClick={() => toggleType(type)} style={{
                    padding: '8px 16px', borderRadius: 10,
                    border: types.includes(type) ? '1px solid #e8363a' : '1px solid rgba(0,0,0,0.08)',
                    background: types.includes(type) ? 'rgba(232,54,58,0.06)' : '#fff',
                    color: types.includes(type) ? '#e8363a' : '#666',
                    fontSize: 13, fontWeight: 500, cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}>
                    {t[type] || type}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Max */}
            <div style={filterSectionStyle}>
              <span style={filterLabelStyle}>{t.priceMax}</span>
              <input
                placeholder={t.max}
                value={priceMax}
                onChange={e => setPriceMax(e.target.value.replace(/\D/g, ''))}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 10,
                  border: '1px solid rgba(0,0,0,0.08)', fontSize: 13,
                  outline: 'none', background: '#f9f9fb',
                }}
              />
            </div>

            {/* Area Min */}
            <div style={filterSectionStyle}>
              <span style={filterLabelStyle}>{t.areaMin}</span>
              <input
                placeholder={t.min}
                value={areaMin}
                onChange={e => setAreaMin(e.target.value.replace(/\D/g, ''))}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 10,
                  border: '1px solid rgba(0,0,0,0.08)', fontSize: 13,
                  outline: 'none', background: '#f9f9fb',
                }}
              />
            </div>

            {/* Bedrooms */}
            {mode !== 'office' && (
              <div style={filterSectionStyle}>
                <span style={filterLabelStyle}>{t.bedrooms}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['', '1', '2', '3', '4', '5'].map(b => (
                    <button key={b} onClick={() => setBeds(b)} style={{
                      flex: 1, padding: '10px 0', borderRadius: 10,
                      border: beds === b ? '1px solid #e8363a' : '1px solid rgba(0,0,0,0.08)',
                      background: beds === b ? 'rgba(232,54,58,0.06)' : '#fff',
                      color: beds === b ? '#e8363a' : '#666',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}>
                      {b === '' ? t.any : b === '5' ? '5+' : b}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* District */}
            {areaList.length > 0 && (
              <div style={filterSectionStyle}>
                <span style={filterLabelStyle}>{t.district}</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                  {areaList.filter(a => a.count > 0).slice(0, 40).map(a => (
                    <button key={a.slug} onClick={() => toggleDistrict(a.name_en)} style={{
                      padding: '6px 12px', borderRadius: 8,
                      border: districts.includes(a.name_en) ? '1px solid #e8363a' : '1px solid rgba(0,0,0,0.06)',
                      background: districts.includes(a.name_en) ? 'rgba(232,54,58,0.06)' : '#f9f9fb',
                      color: districts.includes(a.name_en) ? '#e8363a' : '#888',
                      fontSize: 12, cursor: 'pointer', fontWeight: 500,
                      transition: 'all 0.2s',
                    }}>
                      {a.name_en}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Furnishing */}
            {mode !== 'office' && (
              <div style={filterSectionStyle}>
                <span style={filterLabelStyle}>{t.furnishing}</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { val: '', label: t.any },
                    { val: 'Furnished', label: t.furnished },
                    { val: 'Unfurnished', label: t.unfurnished },
                    { val: 'Partly Furnished', label: t.partly },
                  ].map(f => (
                    <button key={f.val} onClick={() => setFurnishing(f.val)} style={{
                      padding: '10px 14px', borderRadius: 10, textAlign: 'left',
                      border: furnishing === f.val ? '1px solid #e8363a' : '1px solid rgba(0,0,0,0.06)',
                      background: furnishing === f.val ? 'rgba(232,54,58,0.06)' : '#fff',
                      color: furnishing === f.val ? '#e8363a' : '#666',
                      fontSize: 13, fontWeight: 500, cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Nearby POI (office mode) */}
            {isOfficeMode && poiCategories.length > 0 && (
              <div style={filterSectionStyle}>
                <span style={filterLabelStyle}>{t.poi}</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {poiCategories.map(c => (
                    <button key={c.key} onClick={() => togglePoi(c.key)} style={{
                      padding: '6px 12px', borderRadius: 8,
                      border: poiKeys.includes(c.key) ? '1px solid #e8363a' : '1px solid rgba(0,0,0,0.06)',
                      background: poiKeys.includes(c.key) ? 'rgba(232,54,58,0.06)' : '#f9f9fb',
                      color: poiKeys.includes(c.key) ? '#e8363a' : '#888',
                      fontSize: 12, cursor: 'pointer', fontWeight: 500,
                    }}>
                      {POI_ICONS[c.key] || '\ud83d\udccd'} {c.label_zh || c.label_en || c.key}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Min Rating */}
            <div style={filterSectionStyle}>
              <span style={filterLabelStyle}>{t.rating}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {['', '3', '4', '4.5'].map(r => (
                  <button key={r} onClick={() => setMinRating(r)} style={{
                    padding: '6px 12px', borderRadius: 8,
                    border: minRating === r ? '1px solid #e8363a' : '1px solid rgba(0,0,0,0.06)',
                    background: minRating === r ? 'rgba(232,54,58,0.06)' : '#f9f9fb',
                    color: minRating === r ? '#e8363a' : '#888',
                    fontSize: 12, cursor: 'pointer', fontWeight: 500,
                  }}>
                    {r === '' ? t.any : `\u2605 ${r}+`}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort By */}
            <div style={filterSectionStyle}>
              <span style={filterLabelStyle}>{t.sortBy}</span>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 10,
                  border: '1px solid rgba(0,0,0,0.08)', fontSize: 13,
                  outline: 'none', background: '#f9f9fb', cursor: 'pointer',
                  color: '#333',
                }}
              >
                <option value="newest">{t.newest}</option>
                <option value="priceLow">{t.priceLow}</option>
                <option value="priceHigh">{t.priceHigh}</option>
                <option value="areaHigh">{t.areaHigh}</option>
                <option value="perPing">{t.perPing}</option>
                <option value="ratingHigh">{t.ratingHigh}</option>
              </select>
            </div>
          </div>
        )}

        {/* ═══ PROPERTY GRID ═══ */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 80 }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                border: '3px solid rgba(0,0,0,0.06)',
                borderTopColor: '#e8363a',
                animation: 'houseSpinner 1s linear infinite',
                margin: '0 auto 16px',
              }} />
              <style>{`@keyframes houseSpinner { to { transform: rotate(360deg); } }`}</style>
              <div style={{ color: '#999', fontSize: 15 }}>{t.loading}</div>
            </div>
          ) : properties.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: 80,
              background: '#fff', borderRadius: 20,
            }}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>&#127968;</div>
              <p style={{ color: '#999', fontSize: 16 }}>{t.noResults}</p>
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : (showFilters ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'),
                gap: 20,
              }}>
                {properties.map((prop, idx) => {
                  const isCoworking = prop.property_type === 'coworking';
                  const ping = fmtPing(prop.area_sqm);
                  const perPing = fmtPerPing(prop);
                  const lineUrl = buildLineChatUrl(prop);
                  return (
                    <div
                      key={prop.id || idx}
                      onClick={() => navigate(`/h/${slug}/property/${prop.id || idx + 1}`)}
                      style={{
                        background: '#fff', borderRadius: 18, overflow: 'hidden',
                        cursor: 'pointer', transition: 'all 0.35s ease',
                        boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
                        display: 'flex', flexDirection: 'column',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.1)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.04)'; }}
                    >
                      {/* Image */}
                      <div style={{
                        height: 200, position: 'relative',
                        background: GRADIENTS[idx % GRADIENTS.length],
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {(prop.images?.[0]) ? (
                          <img
                            src={prop.images[0].includes('picsum.photos') ? `${API_BASE}/h/${slug}/img-proxy?url=${encodeURIComponent(prop.images[0])}` : prop.images[0]}
                            alt={prop.title}
                            onError={e => { e.target.style.display = 'none'; }}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                          />
                        ) : (
                          <span style={{ fontSize: 40, opacity: 0.25 }}>&#127963;</span>
                        )}
                        <div style={{
                          position: 'absolute', bottom: 12, left: 12,
                          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
                          borderRadius: 8, padding: '6px 14px',
                          color: '#fff', fontSize: 16, fontWeight: 700,
                        }}>
                          {fmtPrice(prop.price, prop.currency)}<span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>{isCoworking ? t.perSqmMonth : t.perMonth}</span>
                        </div>
                        <div style={{
                          position: 'absolute', top: 12, left: 12,
                          background: isCoworking ? 'rgba(114,46,209,0.9)' : 'rgba(22,119,255,0.9)',
                          color: '#fff', backdropFilter: 'blur(8px)',
                          borderRadius: 6, padding: '3px 10px',
                          fontSize: 11, fontWeight: 600,
                        }}>
                          {typeLabel(prop)}
                        </div>
                        {prop.rating_avg ? (
                          <div style={{
                            position: 'absolute', top: 12, right: 12,
                            background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
                            borderRadius: 8, padding: '3px 10px',
                            fontSize: 12, fontWeight: 700, color: '#d4a537',
                            display: 'flex', alignItems: 'center', gap: 2,
                          }}>
                            &#9733; {prop.rating_avg}
                          </div>
                        ) : null}
                      </div>

                      {/* Info */}
                      <div style={{ padding: '16px 18px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <h3 style={{
                          fontSize: 15, fontWeight: 700, color: '#1a1a2e',
                          marginBottom: 6, lineHeight: 1.3,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {prop.title}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 14, color: '#999', fontSize: 13 }}>
                          &#128205; {prop.district || prop.location}
                          {prop.source_type && (
                            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#aaa' }}>
                              {(sourceList.find(s => s.key === prop.source_type)?.name_zh || prop.source_type)}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                          {prop.area_sqm && (
                            <span style={{ background: '#f5f5fa', borderRadius: 6, padding: '3px 8px', fontSize: 12, color: '#666' }}>
                              &#9633; {prop.area_sqm} {t.sqm}
                              {ping && <span style={{ marginLeft: 4 }}>· {ping} {t.ping}</span>}
                            </span>
                          )}
                          {perPing && (
                            <span style={{ background: 'rgba(212,165,55,0.12)', borderRadius: 6, padding: '3px 8px', fontSize: 12, color: '#a07d1f', fontWeight: 600 }}>
                              {t.perPingUnit} {fmtPrice(perPing)}
                            </span>
                          )}
                          {prop.bedrooms != null && (
                            <span style={{ background: '#f5f5fa', borderRadius: 6, padding: '3px 8px', fontSize: 12, color: '#666' }}>
                              &#128716; {prop.bedrooms} {t.beds}
                            </span>
                          )}
                        </div>
                        {prop.nearby_pois && prop.nearby_pois.length > 0 && (
                          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
                            {[...new Set(prop.nearby_pois.map(p => POI_ICONS[p.category] || '\ud83d\udccd'))].slice(0, 5).join(' ')}
                            {' '}{t.nearbyFacilities}
                          </div>
                        )}
                        {prop.nearby_hotels && prop.nearby_hotels.length > 0 && (
                          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
                            {[...new Set(prop.nearby_hotels.map(h => h.type === 'airbnb' ? '\ud83c\udfe1' : '\ud83c\udfe8'))].join(' ')}
                            {' '}{t.nearbyStay} {prop.nearby_hotels.length}
                          </div>
                        )}
                        <div style={{ marginTop: 'auto', display: 'flex', gap: 8, paddingTop: 10 }}>
                          {lineUrl && (
                            <a
                              href={lineUrl}
                              target="_blank"
                              rel="noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{
                                flex: 1, textAlign: 'center', padding: '10px',
                                borderRadius: 10, fontSize: 13, fontWeight: 600,
                                color: '#06c755', border: '1.5px solid #06c755',
                                textDecoration: 'none', transition: 'all 0.2s',
                              }}
                            >
                              &#128172; {t.lineAsk}
                            </a>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/h/${slug}/property/${prop.id || idx + 1}`); }}
                            style={{
                              flex: 1, padding: '10px',
                              borderRadius: 10, fontSize: 13, fontWeight: 600,
                              background: 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                              color: '#fff', border: 'none', cursor: 'pointer',
                            }}
                          >
                            {t.viewDetails}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Load More */}
              {properties.length < total && (
                <div style={{ textAlign: 'center', marginTop: 40 }}>
                  <button
                    onClick={() => { setPage(p => p + 1); fetchProperties(page + 1); }}
                    style={{
                      background: '#fff', border: '1px solid rgba(26,26,46,0.1)',
                      padding: '14px 48px', borderRadius: 12,
                      fontSize: 15, fontWeight: 600, color: '#1a1a2e',
                      cursor: 'pointer', transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={e => { e.target.style.background = '#1a1a2e'; e.target.style.color = '#fff'; }}
                    onMouseLeave={e => { e.target.style.background = '#fff'; e.target.style.color = '#1a1a2e'; }}
                  >
                    {t.loadMore}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
